import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Menu, X } from "lucide-react";
import { Button } from "@/components/ui/button";

export const Navigation = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { href: "/", label: "Főoldal" },
    { href: "/szolgaltatasok", label: "Szolgáltatások" },
    { href: "/referenciak", label: "Referenciák" },
    { href: "/tervezo", label: "Tervezd meg" },
    { href: "/rolam", label: "Rólam" },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <nav 
      data-testid="navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
        isScrolled || isMobileMenuOpen ? "bg-[#030303] border-b border-white/5" : "bg-transparent"
      }`}
    >
      <div className="container mx-auto px-5 sm:px-6 max-w-7xl">
        <div className="flex items-center justify-between h-20">
          
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 flex-shrink-0" data-testid="logo">
            <img 
              src={`${process.env.REACT_APP_BACKEND_URL}/api/images/stryx-portfolio/references/stryx-header-logo.png`}
              alt="Stryx Logo" 
              className="w-9 h-9 object-contain"
            />
            <span className="font-['Cormorant_Garamond'] text-xl text-white tracking-[0.1em] font-medium hidden sm:block">
              STRYX
            </span>
          </Link>

          {/* Desktop Navigation - Center */}
          <div className="hidden xl:flex items-center justify-center gap-8">
            {navLinks.map((link) => (
              <Link
                key={link.href}
                to={link.href}
                className={`text-[0.75rem] tracking-[0.1em] uppercase transition-colors duration-300 ${
                  isActive(link.href) 
                    ? 'text-[#D4AF37]' 
                    : 'text-zinc-400 hover:text-white'
                }`}
                data-testid={`nav-${link.href.replace('/', '') || 'home'}`}
              >
                {link.label}
              </Link>
            ))}
          </div>

          {/* Right Side - Phone & CTA */}
          <div className="hidden xl:flex items-center gap-8 flex-shrink-0">
            <a 
              href="tel:+36305664154" 
              className="flex items-center gap-2 text-zinc-500 hover:text-[#D4AF37] transition-colors"
              data-testid="nav-phone"
            >
              <Phone className="w-4 h-4" strokeWidth={1.5} />
              <span className="text-sm">+36 30 566 4154</span>
            </a>
            
            <Link to="/kapcsolat">
              <Button 
                className="bg-[#D4AF37] hover:bg-[#B5952F] text-black text-[0.7rem] tracking-[0.1em] uppercase font-medium px-6 py-2.5 rounded-full"
                data-testid="nav-cta"
              >
                Kapcsolat
              </Button>
            </Link>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="xl:hidden text-white p-2 hover:text-[#D4AF37] transition-colors"
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {isMobileMenuOpen ? <X className="w-6 h-6" strokeWidth={1.5} /> : <Menu className="w-6 h-6" strokeWidth={1.5} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && (
          <>
            {/* Backdrop - starts below header */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="xl:hidden fixed inset-0 top-20 bg-[#030303] z-[45]"
              onClick={() => setIsMobileMenuOpen(false)}
            />
            {/* Menu Content */}
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              className="xl:hidden absolute top-full left-0 right-0 bg-[#030303] border-t border-white/5 z-[50]"
              data-testid="mobile-menu"
            >
              <div className="container mx-auto px-5 sm:px-6 py-6">
                <div className="flex flex-col gap-1">
                  {navLinks.map((link, idx) => (
                    <motion.div
                      key={link.href}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: idx * 0.05 }}
                    >
                      <Link
                        to={link.href}
                        className={`block py-3 text-lg font-['Cormorant_Garamond'] ${
                          isActive(link.href) ? 'text-[#D4AF37]' : 'text-zinc-300 hover:text-white'
                        } transition-colors`}
                        onClick={() => setIsMobileMenuOpen(false)}
                      >
                        {link.label}
                      </Link>
                    </motion.div>
                  ))}
                </div>
                
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ delay: 0.3 }}
                  className="mt-6 pt-6 border-t border-white/10 flex flex-col gap-4"
                >
                  <a href="tel:+36305664154" className="flex items-center gap-3 text-zinc-400">
                    <Phone className="w-4 h-4" strokeWidth={1.5} />
                    <span>+36 30 566 4154</span>
                  </a>
                  <Link to="/kapcsolat" onClick={() => setIsMobileMenuOpen(false)}>
                    <Button className="bg-[#D4AF37] hover:bg-[#B5952F] text-black text-sm uppercase tracking-wider font-medium w-full py-3 rounded-none">
                      Kapcsolat
                    </Button>
                  </Link>
                </motion.div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </nav>
  );
};
